import { ErrorHandler, Injectable, Provider } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import posthog from 'posthog-js';
import { environment } from '../../environments/environment';

const CONSENT_KEY = 'cookie_consent';
const CHUNK_LOAD_RELOAD_KEY = 'last_chunk_load_reload_time';
const CHUNK_LOAD_RELOAD_DEBOUNCE_MS = 15000;

@Injectable({ providedIn: 'root' })
export class PostHogErrorHandler implements ErrorHandler {
  public handleError(error: unknown): void {
    if (this.isChunkLoadError(error)) {
      this.handleChunkLoadError();
      return;
    }

    if (!this.hasConsent()) {
      return;
    }

    if (environment.env === 'development') {
      console.error('PostHog Error Catcher (dev mode - not sending):', error);
      return;
    }

    const extractedError = this.extractError(error) || 'Unknown error';
    posthog.captureException(extractedError);
  }

  private isChunkLoadError(errorCandidate: unknown): boolean {
    const error = this.unwrapOriginalError(errorCandidate);
    if (!error) {
      return false;
    }

    let message = '';
    let stack = '';

    if (typeof error === 'string') {
      message = error;
    } else if (error && typeof error === 'object') {
      if ('message' in error && typeof error.message === 'string') {
        message = error.message;
      }
      if ('stack' in error && typeof error.stack === 'string') {
        stack = error.stack;
      }
    }

    const combined = `${message} ${stack}`.toLowerCase();

    const chunkLoadErrorMessages = [
      'failed to fetch dynamically imported module',
      'loading chunk',
      'chunkloaderror',
      'error loading dynamically imported module',
    ];

    return chunkLoadErrorMessages.some(msg => combined.includes(msg));
  }

  private handleChunkLoadError(): void {
    // This recovery mechanism is primarily designed for browser-based clients
    // and Electron applications that load remote content. It handles cases
    // where a new deployment may have invalidated old JS chunks. For file-based
    // Electron apps, this error is unlikely and a reload may not resolve the issue.
    if (typeof window === 'undefined' || !window.location || !window.sessionStorage) {
      return;
    }

    const now = Date.now();
    const lastReloadTimeStr = window.sessionStorage.getItem(CHUNK_LOAD_RELOAD_KEY);

    if (lastReloadTimeStr) {
      const lastReloadTime = parseInt(lastReloadTimeStr, 10);
      // If we reloaded less than 15 seconds ago, don't reload again to prevent infinite loop.
      if (now - lastReloadTime < CHUNK_LOAD_RELOAD_DEBOUNCE_MS) {
        console.warn('Chunk load error occurred, but page was reloaded recently. Skipping reload to avoid loop.');
        return;
      }
    }

    try {
      window.sessionStorage.setItem(CHUNK_LOAD_RELOAD_KEY, now.toString());
      console.log('Chunk load error detected. Reloading page to fetch latest assets...');
      window.location.reload();
    } catch (e) {
      console.error('Failed to set sessionStorage or reload page:', e);
    }
  }

  private hasConsent(): boolean {
    if (typeof localStorage === 'undefined') {
      return false;
    }
    try {
      const stored = localStorage.getItem(CONSENT_KEY);
      return stored ? JSON.parse(stored) === true : false;
    } catch {
      return false;
    }
  }

  private extractError(errorCandidate: unknown): unknown {
    const error = this.unwrapOriginalError(errorCandidate);
    if (error instanceof HttpErrorResponse) {
      return this.extractHttpModuleError(error);
    }
    if (typeof error === 'string' || this.isErrorOrErrorLikeObject(error)) {
      return error;
    }
    return null;
  }

  private unwrapOriginalError(error: unknown): unknown {
    if (typeof error !== 'object' || error === null || !('ngOriginalError' in error)) {
      return error;
    }
    return (error as { ngOriginalError: unknown }).ngOriginalError ?? error;
  }

  private extractHttpModuleError(error: HttpErrorResponse): string | Error {
    if (this.isErrorOrErrorLikeObject(error.error)) {
      return error.error;
    }
    if (typeof ErrorEvent !== 'undefined' && error.error instanceof ErrorEvent && error.error.message) {
      return error.error.message;
    }
    if (typeof error.error === 'string') {
      return `Server returned code ${error.status} with body "${error.error}"`;
    }
    return error.message;
  }

  private isErrorOrErrorLikeObject(value: unknown): value is Error {
    if (value instanceof Error) {
      return true;
    }
    if (value === null || typeof value !== 'object' || Array.isArray(value)) {
      return false;
    }
    return 'name' in value && 'message' in value && 'stack' in value;
  }
}

export function providePostHogErrorHandler(): Provider {
  return {
    provide: ErrorHandler,
    useValue: new PostHogErrorHandler(),
  };
}
